'use client'

import { useState } from 'react'
import { ProfileSteps } from '../GuidedIntro/components/profile/ProfileStep'
import {
  ImageIcon,
  LayoutDashboardIcon,
  LockIcon,
  MapPinIcon,
  Share2Icon,
  TagIcon,
  UserIcon,
} from 'lucide-react'
import { AnimatePresence } from 'motion/react'

import { Kicker } from '@/components/Kicker'
import { Button } from '@/components/ui/button'
import {
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption,
} from '@/components/ui/native-select'
import { User } from '@/payload-types'
import { useLenisContext } from '@/providers/Lenis'
import { cn } from '@/utilities/ui'

import { RenderForm } from './components/RenderForm'

export type FormType =
  | 'profile'
  | 'socials'
  | 'location'
  | 'image'
  | 'password'
  | 'topics'
  | 'dashboard'

// Account
const ACCOUNT_ITEMS: { type: FormType; label: string; icon: React.ReactNode }[] = [
  { type: 'password', label: 'Password', icon: <LockIcon className="size-4 shrink-0" /> },
]

const PERSONAL_ITEMS: { type: FormType; label: string; icon: React.ReactNode }[] = [
  { type: 'profile', label: 'Profile', icon: <UserIcon className="size-4 shrink-0" /> },
  { type: 'image', label: 'Profile image', icon: <ImageIcon className="size-4 shrink-0" /> },
  { type: 'socials', label: 'Socials', icon: <Share2Icon className="size-4 shrink-0" /> },
  { type: 'location', label: 'Location', icon: <MapPinIcon className="size-4 shrink-0" /> },
]

const OTHER_ITEMS: { type: FormType; label: string; icon: React.ReactNode }[] = [
  { type: 'topics', label: 'Topics', icon: <TagIcon className="size-4 shrink-0" /> },
  {
    type: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboardIcon className="size-4 shrink-0" />,
  },
]

const ALL_ITEMS = [...ACCOUNT_ITEMS, ...PERSONAL_ITEMS, ...OTHER_ITEMS]

const menuItems = {
  account: ACCOUNT_ITEMS,
  personal: PERSONAL_ITEMS,
  preferences: OTHER_ITEMS,
} as const

function getCategoryForType(type: FormType): string {
  for (const [category, items] of Object.entries(menuItems)) {
    if (items.some((i) => i.type === type))
      return category.charAt(0).toUpperCase() + category.slice(1)
  }
  return ''
}

function getHeadingForType(type: FormType): string {
  return ALL_ITEMS.find((i) => i.type === type)?.label ?? type
}

export const Settings = ({
  user,
  setOpenDrawer: _setOpenDrawer,
  setCurrentProfileStep,
  onUserUpdated,
  initialFormType = 'profile',
}: {
  user: User
  setOpenDrawer: (open: boolean) => void
  setCurrentProfileStep: (step: ProfileSteps) => void
  onUserUpdated: (updatedUser?: User) => Promise<void>
  initialFormType?: FormType
}) => {
  const [selectedFormType, setSelectedFormType] = useState<FormType>(initialFormType)
  const {
    windowDimensions: { width },
  } = useLenisContext()

  const isMobile = width < 768

  return (
    <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      <div className="relative flex size-full flex-col border-t border-border pt-6 font-family-body">
        <div className="flex flex-1 flex-col items-start justify-between pb-8 md:flex-row">
          <h3 className="pb-4 font-family-body text-xl text-neutral-500 max-md:flex max-md:items-center md:w-1/4 md:pb-0 lg:text-2xl xl:pl-6">
            <span className="block font-medium">{getCategoryForType(selectedFormType)}</span>
            <span className="block font-medium md:hidden">:&nbsp;</span>
            <span className="block text-lg">{getHeadingForType(selectedFormType)}</span>
          </h3>
          <div className="md:w-3/4 md:px-8 lg:pl-12">
            <h2 className="font-family-body text-xl font-semibold text-foreground lg:text-2xl">
              Manage your profile, preferences, and account.
            </h2>
            <p className="mt-3 font-body text-card-foreground">
              Customize and update any of the items below.
            </p>
          </div>
        </div>
        <div className="flex w-full flex-1 flex-col justify-end border-t border-border pt-6 md:flex-row">
          <div className="flex w-full flex-col justify-between self-start md:w-1/4">
            <div className="w-full xl:pl-6">
              <AnimatePresence>
                {isMobile ? (
                  <div className="flex w-full flex-col px-1 [&>div]:w-full">
                    <h4 className="mb-2 block font-body text-sm leading-none font-semibold text-card-foreground">
                      Select an item to manage
                    </h4>
                    <NativeSelect
                      value={selectedFormType}
                      onChange={(e) => setSelectedFormType(e.target.value as FormType)}
                      className="w-full [&>select]:h-[50px] [&>select]:bg-input"
                    >
                      {(
                        [
                          ['Personal', PERSONAL_ITEMS],
                          ['Preferences', OTHER_ITEMS],
                          ['Account', ACCOUNT_ITEMS],
                        ] as const
                      ).map(([category, items]) => (
                        <NativeSelectOptGroup key={category} label={category}>
                          {items.map(({ type, label }) => (
                            <NativeSelectOption key={type} value={type}>
                              {label}
                            </NativeSelectOption>
                          ))}
                        </NativeSelectOptGroup>
                      ))}
                    </NativeSelect>
                  </div>
                ) : (
                  <nav className="flex flex-col gap-1 rounded-xl border border-border bg-card px-2 py-4 lg:px-4">
                    <div className="pt-2 pb-4">
                      <Kicker size="small" className="mb-2 w-fit">
                        Personal
                      </Kicker>
                      <ul className="flex flex-col gap-1">
                        {PERSONAL_ITEMS.map(({ type, label, icon }) => (
                          <li key={type} className="flex list-none flex-col gap-1">
                            <Button
                              variant="filter"
                              icon={false}
                              customIcon={icon}
                              onClick={() => setSelectedFormType(type)}
                              className={cn(
                                selectedFormType === type &&
                                  'selected [&.selected]:bg-evergreen-100 [&.selected]:text-accent',
                              )}
                            >
                              <span className="flex items-center gap-2">
                                {icon}
                                <span className="text-sm">{label}</span>
                              </span>
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Kicker size="small" className="mb-2 w-fit">
                      Preferences
                    </Kicker>
                    <ul className="flex flex-col gap-1">
                      {OTHER_ITEMS.map(({ type, label, icon }) => (
                        <li key={type} className="list-none">
                          <Button
                            variant="filter"
                            icon={false}
                            onClick={() => setSelectedFormType(type)}
                            className={cn(
                              selectedFormType === type &&
                                'selected [&.selected]:bg-evergreen-100 [&.selected]:text-accent',
                            )}
                          >
                            <span className="flex items-center gap-2">
                              {icon}
                              <span className="text-sm">{label}</span>
                            </span>
                          </Button>
                        </li>
                      ))}
                    </ul>
                    <div className="pt-2 pb-4">
                      <Kicker size="small" className="mb-2 w-fit">
                        Account
                      </Kicker>
                      <ul className="flex flex-col gap-1">
                        {ACCOUNT_ITEMS.map(({ type, label, icon }) => (
                          <li key={type} className="flex list-none flex-col gap-1">
                            <Button
                              variant="filter"
                              icon={false}
                              customIcon={icon}
                              onClick={() => setSelectedFormType(type)}
                              className={cn(
                                selectedFormType === type &&
                                  'selected [&.selected]:bg-evergreen-100 [&.selected]:text-accent',
                              )}
                            >
                              <span className="flex items-center gap-2">
                                {icon}
                                <span className="text-sm">{label}</span>
                              </span>
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </nav>
                )}
              </AnimatePresence>
              {/* <div>
                <div className="h-8" />
              </div> */}
            </div>
          </div>
          <div className="mb-8 flex flex-col justify-between self-start md:w-3/4">
            <div className="md:px-8 lg:pl-12">
              <div className="min-w-0 flex-1 py-2 lg:py-0 md:[&_.m-auto]:ml-0 [&>div]:border-t-0">
                <RenderForm
                  formType={selectedFormType}
                  user={user}
                  setOpenDrawer={() => {}}
                  setCurrentProfileStep={setCurrentProfileStep}
                  onUserUpdated={onUserUpdated}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
